/**
 * Created with IntelliJ IDEA.
 * User: tschenkel
 * Date: 3/19/14
 * Time: 4:46 PM
 * To change this template use File | Settings | File Templates.
 */
import { AbstractCookie } from "./AbstractCookie";
import { StringUtils } from "../stringhelper/StringUtils";
import { SessionTypes } from "../constants/SessionTypes";

export class NetworkCookie extends AbstractCookie {

    public static ATTRIBUTE_USERNAME: string = "p_username";
    public static ATTRIBUTE_EMAIL: string = "p_email";
    public static ATTRIBUTE_LOGIN_TOKEN: string = "p_loginToken";
    public static ATTRIBUTE_COUNTRY: string = "country";
    public static ATTRIBUTE_PLAYER_ID: string = "playerID";
    public static ATTRIBUTE_INSTANCE_ID: string = "instanceId";
    public static ATTRIBUTE_ZONE_ID: string = "zoneId";
    public static ATTRIBUTE_FACEBOOK_UID: string = "facebookUID";
    public static ATTRIBUTE_CLIENT_VERSION: string = "clientVersion";

    public static ATTRIBUTES: string[] = [
        NetworkCookie.ATTRIBUTE_USERNAME,
        NetworkCookie.ATTRIBUTE_EMAIL,
        NetworkCookie.ATTRIBUTE_LOGIN_TOKEN,
        NetworkCookie.ATTRIBUTE_COUNTRY,
        NetworkCookie.ATTRIBUTE_PLAYER_ID,
        NetworkCookie.ATTRIBUTE_INSTANCE_ID,
        NetworkCookie.ATTRIBUTE_ZONE_ID,
        NetworkCookie.ATTRIBUTE_FACEBOOK_UID,
        NetworkCookie.ATTRIBUTE_CLIENT_VERSION
    ];

    /**
     * Constructor of network cookie.
     */
    constructor() {
        super("nc");
    }

    /**
     * Save username and persistent loginToken.
     */
    public saveLoginData(username: string, loginToken: string): void {
        this.setAttribute(NetworkCookie.ATTRIBUTE_USERNAME, username, false);
        this.setAttribute(NetworkCookie.ATTRIBUTE_LOGIN_TOKEN, loginToken, false);

        // persist changes
        this.writeLocalStorage();
    }

    /**
     * Deletes username and persistent loginToken to "forget" the user.
     */
    public deleteLoginData(): void {
        this.deleteAttribute(NetworkCookie.ATTRIBUTE_USERNAME, false);
        this.deleteAttribute(NetworkCookie.ATTRIBUTE_LOGIN_TOKEN, false);

        // persist changes
        this.writeLocalStorage();
    }

    /**
     * Clear all cookie data.
     * This starts you with a fresh SharedObject, use with caution.
     */
    public clear(): void {
        this.deleteAttribute(NetworkCookie.ATTRIBUTE_USERNAME, false);
        this.deleteAttribute(NetworkCookie.ATTRIBUTE_LOGIN_TOKEN, false);
        this.deleteAttribute(NetworkCookie.ATTRIBUTE_EMAIL, false);
        this.deleteAttribute(NetworkCookie.ATTRIBUTE_COUNTRY, false);
        this.deleteAttribute(NetworkCookie.ATTRIBUTE_INSTANCE_ID, false);
        this.deleteAttribute(NetworkCookie.ATTRIBUTE_PLAYER_ID, false);
        this.deleteAttribute(NetworkCookie.ATTRIBUTE_ZONE_ID, false);
        this.deleteAttribute(NetworkCookie.ATTRIBUTE_FACEBOOK_UID, false);
        this.deleteAttribute(NetworkCookie.ATTRIBUTE_CLIENT_VERSION, false);

        // persist data
        this.writeLocalStorage();
    }

    /**
     * Sets username.
     * @param value The new username to set.
     */
    public set username(value: string) {
        this.setAttribute(NetworkCookie.ATTRIBUTE_USERNAME, value);
    }

    /**
     * Gets the saved username for persistent login.
     * @return The username.
     */
    public get username(): string {
        return this.getAttributeAsString(NetworkCookie.ATTRIBUTE_USERNAME);
    }

    /**
     * Saves persistent login token
     * @param value The new username to set.
     */
    public set loginToken(value: string) {
        this.setAttribute(NetworkCookie.ATTRIBUTE_LOGIN_TOKEN, value);
    }

    /**
     * Gets the saved persistent login token
     * @return The username.
     */
    public get loginToken(): string {
        return this.getAttributeAsString(NetworkCookie.ATTRIBUTE_LOGIN_TOKEN);
    }

    /**
     * Sets email address for current instance
     * @param value The new email address to set.
     */
    public set email(value: string) {
        this.setAttribute(NetworkCookie.ATTRIBUTE_EMAIL, value);
    }

    /**
     * Gets saved email address for persistent login
     * @return The email address.
     */
    public get email(): string {
        return this.getAttributeAsString(NetworkCookie.ATTRIBUTE_EMAIL);
    }

    /**
     * Sets the country attribute.
     * @param value The new country to set.
     */
    public set country(value: string) {
        this.setAttribute(NetworkCookie.ATTRIBUTE_COUNTRY, value);
    }

    /**
     * Gets the country stored in cookie (if available, otherwise returns empty string).
     * @return The country.
     */
    public get country(): string {
        if (this.hasAttribute(NetworkCookie.ATTRIBUTE_COUNTRY)) {
            return this.getAttributeAsString(NetworkCookie.ATTRIBUTE_COUNTRY);
        } else {
            return "";
        }
    }

    /**
     * Sets the playerID.
     * @param value The new PlayerID to set.
     */
    public set playerId(value: number) {
        this.setAttribute(NetworkCookie.ATTRIBUTE_PLAYER_ID, value);
    }

    /**
     * Returns the playerID (if available, otherwise returns 0).
     * @return The playerID.
     */
    public get playerId(): number {
        if (this.hasAttribute(NetworkCookie.ATTRIBUTE_PLAYER_ID)) {
            return this.getAttributeAsNumber(NetworkCookie.ATTRIBUTE_PLAYER_ID);
        } else {
            return 0;
        }
    }

    /**
     * Sets the instance ID attribute and adds the new ID to the list of available instances.
     * @param value The new instance ID to set.
     */
    public set instanceId(value: number) {
        if (value <= -1) {
            value = 0;
        }

        this.setAttribute(NetworkCookie.ATTRIBUTE_INSTANCE_ID, value);
    }

    /**
     * Gets the instanceID (if available, otherwise returns 0).
     * @return The instanceID.
     */
    public get instanceId(): number {
        if (this.hasAttribute(NetworkCookie.ATTRIBUTE_INSTANCE_ID)) {
            return this.getAttributeAsNumber(NetworkCookie.ATTRIBUTE_INSTANCE_ID);
        } else {
            return 0;
        }
    }

    /**
     * Checks if an instance ID is saved in the cookie.
     * @return Whether an instance ID was found.
     */
    public get hasInstanceId(): boolean {
        return this.hasAttribute(NetworkCookie.ATTRIBUTE_INSTANCE_ID) && (this.getAttributeAsInt(NetworkCookie.ATTRIBUTE_INSTANCE_ID) > 0);
    }

    /**
     * Sets the zoneId attribute.
     * @param value The new zoneId to set.
     */
    public set zoneId(value: string) {
        this.setAttribute(NetworkCookie.ATTRIBUTE_ZONE_ID, value);
    }

    /**
     * Gets the zoneId stored in cookie (if available, otherwise returns empty string).
     * @return The zoneId.
     */
    public get zoneId(): string {
        if (this.hasAttribute(NetworkCookie.ATTRIBUTE_ZONE_ID)) {
            return this.getAttributeAsString(NetworkCookie.ATTRIBUTE_ZONE_ID);
        } else {
            return "";
        }
    }

    /**
     * Sets the facebookUID attribute.
     * @param value The new zoneId to set.
     */
    public set facebookUID(value: string) {
        this.setAttribute(NetworkCookie.ATTRIBUTE_FACEBOOK_UID, value);
    }

    /**
     * Gets the facebookUID stored in cookie (if available, otherwise returns empty string).
     * @return The facebookUID.
     */
    public get facebookUID(): string {
        if (this.hasAttribute(NetworkCookie.ATTRIBUTE_FACEBOOK_UID)) {
            return this.getAttributeAsString(NetworkCookie.ATTRIBUTE_FACEBOOK_UID);
        } else {
            return "";
        }
    }

    /**
     * Gets the session count for the current instance.
     * @return The session count.
     */
    public set clientVersion(value: string) {
        this.setAttribute(NetworkCookie.ATTRIBUTE_CLIENT_VERSION + (this.hasInstanceId ? this.instanceId : ""), value);
    }

    /**
     * Gets the session count for the current instance.
     * @return The session count.
     */
    public get clientVersion(): string {
        return this.getAttributeAsString(NetworkCookie.ATTRIBUTE_CLIENT_VERSION + (this.hasInstanceId ? this.instanceId : ""));
    }
}
